import {
  createFileRoute,
  Link,
  useNavigate,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, MoreVertical, Plus } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { z } from 'zod'

import mitraEmptyArt from '@/assets/mascot/mitra-watchlist-empty.png'
import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import Sparkline from '@/components/charts/Sparkline'
import Button from '@/components/ui/Button'
import { buttonVariants } from '@/components/ui/button-variants'
import Change from '@/components/ui/Change'
import Checkbox from '@/components/ui/Checkbox'
import EmptyState from '@/components/ui/EmptyState'
import Field from '@/components/ui/Field'
import IconButton from '@/components/ui/IconButton'
import KeyValueList from '@/components/ui/KeyValueList'
import { MenuActionItem } from '@/components/ui/MenuItem'
import MenuSingle from '@/components/ui/MenuSingle'
import Modal from '@/components/ui/Modal'
import Money from '@/components/ui/Money'
import Popover from '@/components/ui/Popover'
import ScrollArea from '@/components/ui/ScrollArea'
import SearchInput from '@/components/ui/SearchInput'
import SelectTrigger from '@/components/ui/SelectTrigger'
import Skeleton from '@/components/ui/Skeleton'
import StockAvatar from '@/components/ui/StockAvatar'
import TitleRow from '@/components/ui/TitleRow'
import { toast } from '@/components/ui/toast-store'
import NewAlertModal from '@/features/alerts/components/NewAlertModal'
import type { CompanyDetail } from '@/features/company/api/companyDetail'
import { companyDetailQueryOptions } from '@/features/company/api/queries'
import UpgradeRequired from '@/features/subscriptions/components/UpgradeRequired'
import { upgradeToast } from '@/features/subscriptions/lib/upgradeToast'
import { subscriptionRequiredFeature } from '@/features/subscriptions/lib/paywall'
import { useCreateWatchlist, useSetWatchlistSymbols } from '@/features/watchlist/api/mutations'
import {
  useCandidateSearch,
  useSuspenseWatchlistCollection,
} from '@/features/watchlist/api/queries'
import { EM_DASH, formatRupees } from '@/lib/broker-format'
import { formatMoney, formatVolume } from '@/lib/format'
import type { Money as MoneyValue } from '@/lib/money'
import { cn } from '@/lib/utils'
import type { WatchRow } from '@/schemas/watchlist'
import { loadWatchlist } from '@/screens/app/WatchlistScreen.loader'

export const Route = createFileRoute('/_app/watchlist')({
  validateSearch: z.object({
    // The sidebar's WATCHLIST group (`watchlistGroup()`, `nav-groups.ts`)
    // serialises this as a STRING (`?list=%2212%22`), but a hand-typed
    // `?list=12` parses as a NUMBER — accept either and normalise to the
    // string `WatchlistRef.id` shape. Absent or malformed both fall through
    // to `undefined`, which the screen reads as "the first list".
    list: z
      .union([z.string(), z.number()])
      .transform((value) => String(value))
      .optional()
      .catch(undefined),
  }),
  loader: loadWatchlist,
  pendingComponent: WatchlistPending,
  errorComponent: WatchlistError,
  component: WatchlistScreen,
})

/**
 * The title row the pending and error pages share: the real title, and a bar
 * where the 36px "New watchlist" pill goes — that pill opens a modal the
 * screen owns, so neither page can offer it, but both keep its height.
 */
const HEADER_SKELETON = (
  <TitleRow title="Watchlist" actions={<Skeleton className="h-11 w-39 rounded-md" />} />
)

/**
 * One list row's skeleton — and the loaded row's frame: the hairline under a
 * 56px two-line row whose hover fill bleeds 12px past the card's inset. Both
 * draw from here so a pending page cannot drift from the page it becomes.
 */
const ROW_BOX = 'border-t border-border-subtle'
const ROW_LINE = '-mx-3 flex h-17 items-center gap-2 px-3 transition-colors duration-fast'

/**
 * The loaded page's frame, measured at 1440 (the 844px column) and at 390
 * (366): the title row, the toolbar search (36 from lg, 44 below), and the
 * list card — `px-5 py-4`, the table-standard two-line rows (56px) and the
 * 36px "Add company" button right-aligned 16px under them. The row count is
 * the list's, so this draws a typical five.
 */
function WatchlistPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading your watchlist…"
      header={HEADER_SKELETON}
      pageTitle="Watchlist"
    >
      <div className="flex flex-col gap-4">
        <Skeleton className="mt-2 mb-4 h-11 w-full rounded-sm" />
        <div>
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className={ROW_BOX}>
              <div className={ROW_LINE}>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Skeleton className="size-10 shrink-0 rounded-full" />
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Skeleton className="h-4 w-16 rounded-xs" />
                    <Skeleton className="h-3 w-40 rounded-xs" />
                  </div>
                  <Skeleton className="hidden h-9 w-25 shrink-0 rounded-xs @sm:block" />
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <Skeleton className="h-4 w-16 rounded-xs" />
                    <Skeleton className="h-3 w-12 rounded-xs" />
                  </div>
                </div>
                <Skeleton className="size-8 shrink-0 rounded-full" />
                <Skeleton className="size-8 shrink-0 rounded-full" />
              </div>
            </div>
          ))}
          <div className="flex justify-end border-t border-border-subtle pt-4">
            <Skeleton className="h-10 w-40 rounded-md" />
          </div>
        </div>
      </div>
    </ScreenLayout>
  )
}

/** The copy this page gave a failed first load before it had a loader. */
function WatchlistError(props: ErrorComponentProps) {
  // `collection()` reads the lists and each list's stocks (both gated,
  // CONTRACT §7); a refusal on either surfaces here as one thrown error.
  const feature = subscriptionRequiredFeature(props.error)
  if (feature !== null) {
    return (
      <ScreenLayout header={HEADER_SKELETON} pageTitle="Watchlist">
        <UpgradeRequired featureKey={feature} />
      </ScreenLayout>
    )
  }
  return (
    <RouteErrorPage
      {...props}
      header={HEADER_SKELETON}
      title="Watchlists did not load"
      description="Check your connection and try again."
    />
  )
}

/** Sentinel option in the "Adding to" menu — see its own comment below. */
const NEW_LIST_OPTION = '__new-list__'

/**
 * The row's own "⋮". Three real, already-wired operations: jump to the alert
 * form, move the symbol to another of the member's lists (an add to that list,
 * then a drop from this one — see `moveToList`) and drop it from this list.
 * "Move to" has one row per OTHER list, so a member with a single list sees
 * only the two.
 */
function RowMenu({
  listName,
  moveTargets,
  onAddAlert,
  onMove,
  onRemove,
}: {
  listName: string
  moveTargets: readonly { id: string; name: string }[]
  onAddAlert: () => void
  onMove: (targetId: string) => void
  onRemove: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      hasPopup="menu"
      label="Row actions"
      className="w-50"
      trigger={(triggerProps) => (
        <IconButton
          {...triggerProps}
          aria-label="Row actions"
          // rest:ub — mock `wl-kebab`: the glyph rests in the quiet grey, goes white on a raised
          // inset square on hover, keyboard focus and while its menu is open (`is-open`).
          className="size-9 rounded-none text-icon-quiet hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground"
        >
          <MoreVertical className="size-4" aria-hidden="true" />
        </IconButton>
      )}
    >
      <MenuActionItem
        label="Add alert"
        tabIndex={-1}
        onClick={() => {
          setOpen(false)
          onAddAlert()
        }}
      />
      {moveTargets.map((target) => (
        <MenuActionItem
          key={target.id}
          label={`Move to ${target.name}`}
          tabIndex={-1}
          onClick={() => {
            setOpen(false)
            onMove(target.id)
          }}
        />
      ))}
      <MenuActionItem
        label={`Remove from ${listName}`}
        danger
        tabIndex={-1}
        onClick={() => {
          setOpen(false)
          onRemove()
        }}
      />
    </Popover>
  )
}

/** A figure the company overview left null: a quiet dash, never a zero. */
const NOT_REPORTED = (
  <>
    <span aria-hidden="true" className="text-muted-foreground">
      {EM_DASH}
    </span>
    <span className="sr-only">Not reported</span>
  </>
)

const price = (value: MoneyValue | null) => (value === null ? NOT_REPORTED : formatMoney(value))

/** One end of the 52-week range: a missing end is a bare dash inside the pair. */
const rangeEnd = (value: MoneyValue | null) => (value === null ? EM_DASH : formatMoney(value))

/** "604.00 / 428.00" — a range with neither end is not reported. */
const yearRange = (company: CompanyDetail | null) =>
  company === null || (company.week52High === null && company.week52Low === null)
    ? NOT_REPORTED
    : `${rangeEnd(company.week52High)} / ${rangeEnd(company.week52Low)}`

/** One labelled figure of the opened row's Open / High / Low / Volume strip. */
function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="text-body-xs text-muted-foreground">{label}</dt>
      <dd className="tnum text-h-s">{value}</dd>
    </div>
  )
}

/**
 * What an opened row adds to the list's own price: the session's open, high,
 * low and volume, then the 52-week range and the market cap — all from
 * `/company/{symbol}/overview/`, the same read the Stock page's Price info
 * draws from (so its cache entry is shared, and opening the stock afterwards
 * costs nothing). One request per opened row, made when it opens rather than
 * for every row of a list that can run to hundreds.
 */
function RowDetail({ symbol, onAddAlert }: { symbol: string; onAddAlert: () => void }) {
  const detail = useQuery(companyDetailQueryOptions(symbol))
  // A failed refetch keeps what it already had on screen; only a first read
  // with nothing to show becomes the failed state.
  const company = detail.data
  const quote = company?.quote ?? null

  return (
    <div className="mb-3 flex flex-col gap-4 bg-card p-4 @lg:flex-row @lg:items-start @lg:gap-6">
      <div className="min-w-0 flex-1">
        {detail.isPending ? (
          <div
            role="status"
            aria-label={`Loading ${symbol} details`}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-2 gap-4 @md:grid-cols-4">
              {Array.from({ length: 4 }, (_, index) => (
                <div key={index} className="flex flex-col gap-1">
                  <Skeleton className="h-4 w-10 rounded-xs" />
                  <Skeleton className="h-5 w-16 rounded-xs" />
                </div>
              ))}
            </div>
            <Skeleton className="h-18 w-full rounded-xs" />
          </div>
        ) : company === undefined ? (
          <EmptyState
            kind="failed"
            size="inline"
            title={`${symbol} details did not load`}
            description="Check your connection and try again."
            onRetry={() => {
              void detail.refetch()
            }}
            retrying={detail.isFetching}
          />
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-4 @md:grid-cols-4">
              <Fact label="Open" value={quote === null ? NOT_REPORTED : price(quote.open)} />
              <Fact label="High" value={quote === null ? NOT_REPORTED : price(quote.high)} />
              <Fact label="Low" value={quote === null ? NOT_REPORTED : price(quote.low)} />
              <Fact
                label="Volume"
                value={
                  company === null || company.volume === null
                    ? NOT_REPORTED
                    : `${formatVolume(company.volume)} kitta`
                }
              />
            </dl>
            <KeyValueList
              className="mt-4 border-t border-border-subtle"
              rows={[
                {
                  label: '52-week high / low',
                  value: yearRange(company),
                },
                {
                  label: 'Market capitalisation',
                  value:
                    company === null || company.marketCap === null
                      ? NOT_REPORTED
                      : formatRupees(company.marketCap),
                },
              ]}
            />
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-2 @lg:w-32 @lg:shrink-0 @lg:flex-col">
        <button
          type="button"
          onClick={onAddAlert}
          className={cn(buttonVariants({ variant: 'secondary' }), '@lg:w-full')}
        >
          <Plus className="size-4" aria-hidden="true" /> Add alert
        </button>
        <Link
          to="/stocks/$symbol"
          params={{ symbol }}
          className={cn(buttonVariants({ variant: 'primary' }), '@lg:w-full')}
        >
          Open {symbol}
        </Link>
      </div>
    </div>
  )
}

/**
 * rest:ub — the opened row's panel eases open and shut (mock `grid-template-rows 0fr → 1fr` over the
 * slow step, ease-out). The content mounts on open, so a closed row costs no overview request, and
 * stays mounted until the close has played. Reduced motion has no transition: it swaps at once.
 */
function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  const [mounted, setMounted] = useState(open)
  const [shown, setShown] = useState(false)
  if (open && !mounted) setMounted(true)

  useEffect(() => {
    // `shown` flips a frame after the mount (open) or at once (close), so the grid has a collapsed
    // frame to ease from; the unmount waits out the slow step.
    const frame = requestAnimationFrame(() => {
      setShown(open)
    })
    const timer = open
      ? undefined
      : setTimeout(() => {
          setMounted(false)
        }, 480)
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [open])

  if (!mounted) return null
  return (
    <div
      className={cn(
        'grid transition-[grid-template-rows] duration-slow ease-out motion-reduce:transition-none',
        shown ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  )
}

function WatchlistRow({
  row,
  listName,
  moveTargets,
  expanded,
  onToggle,
  onAddAlert,
  onMove,
  onRemove,
}: {
  row: WatchRow
  listName: string
  moveTargets: readonly { id: string; name: string }[]
  expanded: boolean
  onToggle: () => void
  onAddAlert: () => void
  onMove: (targetId: string) => void
  onRemove: () => void
}) {
  return (
    <div className={ROW_BOX}>
      <div
        className={cn(
          ROW_LINE,
          // rest:ub — the mock's soft wash (60% of the raised grey), the whole 68px row a pointer.
          'group/row cursor-pointer hover:bg-table-row-hover-soft has-focus-visible:bg-table-row-hover-soft',
        )}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-3 py-2 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control"
        >
          <StockAvatar symbol={row.symbol} logoUrl={row.logo} size={40} className="p-2" />
          <span className="min-w-0 flex-1">
            <span
              className="block truncate text-h-s text-foreground underline-offset-3 group-hover/row:underline"
              title={row.symbol}
            >
              {row.symbol}
            </span>
            <span className="block truncate text-body-xs text-muted-foreground" title={row.name}>
              {row.name}
            </span>
          </span>
          <Sparkline
            points={row.sparkline}
            width={100}
            height={20}
            strokeWidth={1.5}
            endDot={false}
            className="hidden @sm:block"
          />
          <span className="flex shrink-0 flex-col items-end">
            <Money value={row.ltp} className="text-mono-m" />
            <Change value={row.percentChange} percent className="text-label-s" />
          </span>
        </button>
        <RowMenu
          listName={listName}
          moveTargets={moveTargets}
          onAddAlert={onAddAlert}
          onMove={onMove}
          onRemove={onRemove}
        />
        {/* A separate chevron beside the "⋮", not folded into it. The row
            button above still toggles too (a bigger hit target for the same
            action), so this is an addition, not a behaviour change. */}
        <IconButton
          aria-label={expanded ? 'Collapse row' : 'Expand row'}
          aria-expanded={expanded}
          onClick={onToggle}
          // rest:ub — the mock's 36 round chevron: an inset fill on hover, the glyph turning over
          // the med step on the out curve.
          className="size-9 shrink-0 hover:bg-muted focus-visible:bg-muted"
        >
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'size-5 transition-transform duration-med ease-out motion-reduce:transition-none',
              expanded && 'rotate-180',
            )}
          />
        </IconButton>
      </div>

      <Collapse open={expanded}>
        <RowDetail symbol={row.symbol} onAddAlert={onAddAlert} />
      </Collapse>
    </div>
  )
}

function WatchlistScreen() {
  const { list: listParam } = Route.useSearch()
  const navigate = Route.useNavigate()
  const routerNavigate = useNavigate()
  const seePlans = () => {
    void routerNavigate({ to: '/subscription' })
  }
  // Never pending — the loader has cached it — and never an error while it
  // has data: a failed refetch keeps the lists on screen.
  const query = useSuspenseWatchlistCollection()
  const setSymbols = useSetWatchlistSymbols()
  const createList = useCreateWatchlist()
  // The sidebar's WATCHLIST group is the switcher now — `listParam` is the
  // URL's own `?list=`, and picking a list navigates rather than setting
  // local state, so a sidebar link and this in-page strip land on the same
  // list. Falls back to the first list when the URL names none (or a stale
  // id no member's collection has any more).
  const setActiveId = (id: string) => {
    void navigate({ search: (prev) => ({ ...prev, list: id }) })
  }
  const [expandedSymbol, setExpandedSymbol] = useState<string>()
  const [search, setSearch] = useState('')
  const [candidateSearch, setCandidateSearch] = useState('')
  const [draftSymbols, setDraftSymbols] = useState<string[]>([])
  const [addOpen, setAddOpen] = useState(false)
  const [addTargetId, setAddTargetId] = useState<string>()
  const [addTargetOpen, setAddTargetOpen] = useState(false)
  const [newListOpen, setNewListOpen] = useState(false)
  const [newListName, setNewListName] = useState('')
  // "when i click on the add alert it takes me to the alert page and open
  // the modal. its weird. keep it on the same page" — `NewAlertModal` opens
  // right here instead, so the list, the expanded row and the scroll stay
  // put. `undefined` symbol keeps the modal closed.
  const [alertSymbol, setAlertSymbol] = useState<string>()
  const searchRef = useRef<HTMLInputElement>(null)
  const newListFormId = useId()

  const lists = query.data.lists
  const active = lists.find((list) => list.id === listParam) ?? lists[0]
  // Switching lists (sidebar or a hand-typed `?list=`) starts each list fresh
  // — a search term or an expanded row from the PREVIOUS list would otherwise
  // survive onto one it was never typed against. Adjusted during render
  // (React's own pattern for "reset state when a prop changes"), not in an
  // effect: an effect would set state a beat after the switch, letting the
  // stale search/expansion paint for one frame against the new list's rows.
  const [resetFor, setResetFor] = useState(active?.id)
  if (active?.id !== resetFor) {
    setResetFor(active?.id)
    setSearch('')
    setExpandedSymbol(undefined)
  }
  const addTarget = lists.find((list) => list.id === addTargetId) ?? active
  // Built once rather than an `includes` per candidate row: "Popular now" is
  // `/market/live/`'s top 200 and a member's list can run to hundreds too.
  const addTargetMembers = new Set(addTarget?.rows.map((row) => row.symbol))
  const visibleRows =
    active?.rows.filter((row) =>
      `${row.symbol} ${row.name}`.toLowerCase().includes(search.trim().toLowerCase()),
    ) ?? []
  /**
   * The picker's rows, and WHICH source they come from is the fix.
   *
   * Nothing typed → "Popular now": `query.data.candidates`, which is
   * `/market/live/`'s 200 most-traded names. That is what the caption
   * promises, so showing them unfiltered is honest.
   *
   * Anything typed → `/market/search/`, which reaches every active company
   * (`watchlistService.searchCandidates`). This screen used to filter those
   * same 200 live rows in memory and answer "No companies found" for the
   * roughly hundred companies outside them — telling a member a company does
   * not exist when it does. There is no partial set left to derive an answer
   * from: the empty state below now means the backend found no match.
   */
  const candidateResults = useCandidateSearch(candidateSearch)
  const candidates: readonly { symbol: string; name: string }[] =
    candidateResults.status === 'idle'
      ? query.data.candidates
      : candidateResults.status === 'ready'
        ? candidateResults.rows
        : []
  /**
   * "Popular now" (`4243:78928`) is the IDLE caption and only the idle caption:
   * it is true of `/market/live/`'s top 200 and false of a search hit, which
   * may be the quietest name on the board. Labelling search results "popular"
   * would be a second small untruth on top of the one being fixed here.
   *
   * One binding, two readers — the visible caption and the scroll region's
   * accessible name, which would otherwise be able to disagree.
   */
  const candidateCaption = candidateResults.status === 'idle' ? 'Popular now' : 'Search results'

  const addAlertFor = (symbol: string) => {
    setAlertSymbol(symbol)
  }

  const removeFromActiveList = (symbol: string) => {
    if (active === undefined) return
    setSymbols.mutate({
      id: active.id,
      symbols: active.rows.map((row) => row.symbol).filter((value) => value !== symbol),
    })
  }

  /** A failed write: the plan-limit toast when mm-be says the plan is the reason, else `message`. */
  const reportWriteFailure = (error: unknown, message: string) => {
    const feature = subscriptionRequiredFeature(error)
    if (feature === null) toast.error(message)
    else upgradeToast(feature, seePlans)
  }

  /**
   * "Move to <list>": an add to the target, then a drop from this list — in
   * that order, and the drop only once the add has landed, so a refused or
   * failed add never costs the member the name. `setSymbols` replaces a list's
   * membership, so each call carries the list's whole new set (the target's
   * existing names first, as the add modal merges them). The two calls have
   * their own toasts: an add that landed and a drop that did not leaves the
   * name on BOTH lists, which "Could not move" would not say.
   */
  const moveToList = async (symbol: string, targetId: string) => {
    const target = lists.find((list) => list.id === targetId)
    if (active === undefined || target === undefined) return
    try {
      await setSymbols.mutateAsync({
        id: target.id,
        symbols: [...new Set([...target.rows.map((row) => row.symbol), symbol])],
      })
    } catch (error) {
      reportWriteFailure(error, `Could not move ${symbol} to ${target.name}`)
      return
    }
    try {
      await setSymbols.mutateAsync({
        id: active.id,
        symbols: active.rows.map((row) => row.symbol).filter((value) => value !== symbol),
      })
    } catch (error) {
      reportWriteFailure(error, `Added ${symbol} to ${target.name}, but it is still on this list`)
    }
  }

  // `draftSymbols` is "names I am ADDING", never the target's membership. It
  // opened seeded with the target's existing members, which made this modal a
  // membership editor wearing an adder's clothes: a list of eight said "8
  // selected" before anyone touched it, and unticking a row you had not picked
  // removed it — under a button that says "Add to Main". Removal lives on the
  // main screen (`removeFromActiveList`) and only there.
  const openAdd = () => {
    // The modal reads its error from `setSymbols`, which the row actions (Move, Remove) share: a
    // failure left by one of those must not open as the modal's own.
    setSymbols.reset()
    setAddTargetId(active?.id)
    setDraftSymbols([])
    setCandidateSearch('')
    setAddOpen(true)
  }

  const selectAddTarget = (id: string) => {
    if (id === NEW_LIST_OPTION) {
      setAddOpen(false)
      setNewListOpen(true)
      return
    }
    setAddTargetId(id)
    // Retargeting KEEPS the picks — the member chose those names, and changing
    // where they land is not a reason to discard them. Anything the new target
    // already holds drops out, so it renders as a locked row below instead of
    // being counted a second time in the footer.
    const members = new Set(lists.find((list) => list.id === id)?.rows.map((row) => row.symbol))
    setDraftSymbols((previous) => previous.filter((symbol) => !members.has(symbol)))
  }

  const toggleDraft = (symbol: string, checked: boolean) => {
    setDraftSymbols((previous) =>
      checked ? [...new Set([...previous, symbol])] : previous.filter((value) => value !== symbol),
    )
  }

  return (
    <ScreenLayout
      pageTitle="Watchlist"
      header={
        <TitleRow
          title="Watchlist"
          actions={
            // V2 (`4236:113812`) moves list creation up here — "Add a
            // company" is now the card's OWN bottom action (per list),
            // since this button no longer has a single list to add into
            // before one is on screen.
            <Button
              type="button"
              variant="secondary"
              aria-haspopup="dialog"
              // rest:ub — the mock's outlined button rests on the quiet edge (#8a8a8a) and lights it
              // to the secondary grey on hover; the fill stays the kit's.
              className="h-11 border-muted-foreground-subtle hover:border-muted-foreground"
              onClick={() => {
                setNewListOpen(true)
              }}
            >
              <Plus className="mr-2 size-4" aria-hidden="true" /> New watchlist
            </Button>
          }
        />
      }
    >
      <Modal
        open={addOpen}
        onOpenChange={setAddOpen}
        initialFocusRef={searchRef}
        title="Add to watchlist"
        // Counts the NEW picks only, so a freshly opened modal reads
        // "0 selected" however full the target already is.
        footerNote={`${String(draftSymbols.length)} selected`}
        footer={
          <>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => {
                setAddOpen(false)
              }}
            >
              Cancel
            </Button>
            <Button
              size="lg"
              disabled={
                setSymbols.isPending || addTarget === undefined || draftSymbols.length === 0
              }
              onClick={() => {
                if (addTarget === undefined) return
                // MERGE, never replace. `setSymbols` diffs the payload against
                // what the list holds server-side and DELETEs whatever is
                // missing from it (`watchlistService.setSymbols`), so sending
                // the draft alone would silently empty the list of everything
                // the member did not re-tick. Existing members first, new picks
                // after, deduplicated.
                setSymbols.mutate(
                  {
                    id: addTarget.id,
                    symbols: [...new Set([...addTargetMembers, ...draftSymbols])],
                  },
                  {
                    onSuccess: () => {
                      setAddOpen(false)
                    },
                    onError: (error) => {
                      const feature = subscriptionRequiredFeature(error)
                      if (feature === null) return
                      setAddOpen(false)
                      upgradeToast(feature, seePlans)
                    },
                  },
                )
              }}
            >
              {setSymbols.isPending ? 'Adding…' : `Add to ${addTarget?.name ?? 'list'}`}
            </Button>
          </>
        }
      >
        {/* `flex-auto`, not `flex-1`: same WebKit fit-content column-flexbox
            gap as Modal's own body — see Modal.tsx. */}
        <div className="flex min-h-0 flex-auto flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <span className="text-label-s text-muted-foreground">Adding to</span>
            {/* V2 (`4243:78928`) draws this row side by side — the label
                left, a content-sized trigger right — not the label-above,
                full-width trigger this screen had before. The trigger keeps
                its own accessible name now instead of borrowing "Adding to"
                from a wrapping `<label>` (see WatchlistScreen.test.tsx).

                V2 (`4243:80695`) opens this as a dropdown rather than the
                static "Adding to {list}" line this app printed before — a
                member can retarget the pick without closing the modal, and the
                names already ticked come along (`selectAddTarget`). The
                sentinel option below closes this modal and opens "New
                watchlist" instead of trying to nest two dialogs.

                `+ New list` shares this menu with the real lists, separated
                only by its `+`. `MenuOption` is `{value, label, count,
                disabled}` and `MenuSingle` maps every one of them to a single
                `MenuItem` — neither can express a separator or a group without
                widening two shared components for this one caller, so the glyph
                stays as the only divider.

                `align="end"` is the frame's own shape: the panel hangs off
                the trigger's RIGHT edge and, at the default 260px panel
                width, reaches well past its left edge — over the search
                field beneath — rather than a same-width dropdown pinned
                under the trigger's left edge. */}
            <MenuSingle
              options={[
                ...lists.map((list) => ({
                  value: list.id,
                  label: `${list.name} · ${String(list.rows.length)} name${list.rows.length === 1 ? '' : 's'}`,
                })),
                { value: NEW_LIST_OPTION, label: '+ New list' },
              ]}
              value={addTargetId ?? null}
              onChange={selectAddTarget}
              open={addTargetOpen}
              onOpenChange={setAddTargetOpen}
              label="Watchlist to add to"
              searchable={false}
              align="end"
              trigger={(triggerProps) => (
                <SelectTrigger
                  {...triggerProps}
                  open={addTargetOpen}
                  // rest:ub — hover = a default edge plus an inset hairline (mock `box-shadow`),
                  // open = the accent's hairline tint, the chevron turning over the med step.
                  openTone="line"
                  className="hover:border-border hover:shadow-[inset_0_0_0_1px_var(--border-strong)]"
                  // `filled` means "a selection is active, so the summary reads
                  // in accent" (SelectTrigger.tsx) — unconditionally set, the
                  // DEFAULT target shouted in green and pulled the eye off the
                  // search box, which is where a member opening an adder is
                  // going. Accent only once the target is no longer the list
                  // they opened this from, where the green is news.
                  filled={addTarget?.id !== active?.id}
                  label={
                    addTarget
                      ? `${addTarget.name} · ${String(addTarget.rows.length)} name${addTarget.rows.length === 1 ? '' : 's'}`
                      : 'Choose a list'
                  }
                />
              )}
            />
          </div>
          <Field
            ref={searchRef}
            type="search"
            aria-label="Search a company or symbol"
            placeholder="Search a company or symbol"
            value={candidateSearch}
            onChange={(event) => {
              setCandidateSearch(event.target.value)
            }}
          />
          {/* The caption belongs to the list under it, not to the column: the
              two are one unit with their own gap (4-grid: 6px snapped to 8),
              which is what the `-mt-2` here used to buy by fighting the
              column's own gap. Grouping says it once instead of subtracting
              it back. */}
          <div className="flex min-h-0 flex-auto flex-col gap-2">
            <p className="shrink-0 text-label-s text-muted-foreground">{candidateCaption}</p>
            {/* The list scrolls on its own so the search field stays in reach,
                and it says so: `ScrollArea` draws a fade over the edge there is
                more past, which clears the moment you reach it. Without one the
                last row was sliced flush at the body's edge with nothing to
                suggest anything followed it.

                `fade="popover"` because this panel is `bg-popover` — fading into
                `card` would leave a pale band floating on it.

                The 4px bleed-and-pad pair is the same one Modal's own body uses
                (and `ToolPickerDialog`'s list): the rows sit at the panel's 20px
                gutter so their hover fill lines up with it, and their focus ring
                still has 4px inside the scroller before it would be clipped. The
                padding has to be INSIDE the scrolling element, so it is a plain
                wrapper rather than `className` — that lands on ScrollArea's
                outer box, where the ring would be outside the viewport. */}
            <ScrollArea fade="popover" label={candidateCaption} className="-mx-1 min-h-0 flex-auto">
              <div className="px-1">
                {candidates.map((row) => {
                  // Already in the target: ticked, and locked so it cannot be
                  // unticked. No path through an ADDER may remove a name — that
                  // is `removeFromActiveList` on the main screen.
                  const locked = addTargetMembers.has(row.symbol)
                  const checked = locked || draftSymbols.includes(row.symbol)
                  return (
                    // The row, not just the Checkbox, is the click target — see
                    // MenuItem's multi row for the same shape: the row forwards
                    // the click and the Checkbox (pointer-events-none) is purely
                    // visual, so there is still exactly one accessible control.
                    // Keyboard still lands on the Checkbox's own input, which is
                    // why the ring below is `has-[:focus-visible]` rather than a
                    // `tabIndex`/`onKeyDown` pair on the div.
                    //
                    // A locked row is NOT a click target: no handler, no hover
                    // fill, no pointer cursor, and its disabled Checkbox takes
                    // itself out of the tab order — rather than a live-looking
                    // row that swallows clicks and does nothing.
                    //
                    // `min-h-11` + `py-2` where a bare `min-h-[53px]` used to
                    // be: 53px was the two lines (20px + 17px) plus this exact
                    // padding, spelled as a total. Stated as padding, the rhythm
                    // is the row's own and 44px is a floor rather than the whole
                    // story.
                    <div
                      key={row.symbol}
                      className={cn(
                        'flex min-h-11 items-center gap-3 rounded-sm px-2 py-2 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring',
                        locked ? 'cursor-default' : 'cursor-pointer hover:bg-muted/70',
                      )}
                      onClick={
                        locked
                          ? undefined
                          : () => {
                              toggleDraft(row.symbol, !checked)
                            }
                      }
                    >
                      <div className="min-w-0 flex-1">
                        <p className={cn('text-h-s', locked && 'text-muted-foreground/60')}>
                          {row.symbol}
                        </p>
                        <p className="truncate text-body-xs text-muted-foreground" title={row.name}>
                          {row.name}
                        </p>
                      </div>
                      {/* Says WHY the box is inert. The dimmed, disabled
                          checkbox alone reads as "unavailable", which is the
                          wrong fact — this name is already being watched. */}
                      {locked ? (
                        <span className="shrink-0 text-label-s text-muted-foreground">Added</span>
                      ) : null}
                      <Checkbox
                        checked={checked}
                        disabled={locked}
                        // What ticking DOES, not a statement of fact — this is a
                        // control, and it used to be labelled "PFL in main".
                        // A locked row has no action to name, so it says the
                        // fact instead.
                        aria-label={
                          locked
                            ? `${row.symbol} is already in ${addTarget?.name ?? 'list'}`
                            : `Add ${row.symbol} to ${addTarget?.name ?? 'list'}`
                        }
                        onCheckedChange={(next) => {
                          toggleDraft(row.symbol, next)
                        }}
                        className="pointer-events-none"
                      />
                    </div>
                  )
                })}
                {candidateResults.status === 'loading' ? (
                  <div className="space-y-2 py-1">
                    <Skeleton className="h-11 w-full" />
                    <Skeleton className="h-11 w-full" />
                    <Skeleton className="h-11 w-full" />
                  </div>
                ) : candidateResults.status === 'failed' ? (
                  // Not "No companies found". The search did not run, which is a
                  // different fact, and the old screen had no way to tell them
                  // apart because it never asked anyone.
                  <EmptyState
                    kind="failed"
                    size="inline"
                    title="Search did not run"
                    description="Check your connection and type the name again."
                    onRetry={candidateResults.retry}
                    retrying={candidateResults.retrying}
                  />
                ) : candidates.length === 0 ? (
                  <EmptyState kind="no-results" size="inline" title="No companies found" />
                ) : null}
              </div>
            </ScrollArea>
          </div>
          {setSymbols.isError && subscriptionRequiredFeature(setSymbols.error) === null ? (
            <p role="alert" className="text-body-s text-destructive">
              {setSymbols.error.message}
            </p>
          ) : null}
        </div>
      </Modal>

      <NewAlertModal
        open={alertSymbol !== undefined}
        onClose={() => {
          setAlertSymbol(undefined)
        }}
        {...(alertSymbol ? { presetSymbol: alertSymbol } : {})}
      />

      <Modal
        open={newListOpen}
        onOpenChange={setNewListOpen}
        title="New watchlist"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => {
                setNewListOpen(false)
              }}
            >
              Cancel
            </Button>
            {/* Outside the <form>, so tied to it by id: the footer is the
                modal's, not the body's. */}
            <Button type="submit" size="lg" form={newListFormId} disabled={createList.isPending}>
              {createList.isPending ? 'Creating…' : 'Create list'}
            </Button>
          </>
        }
      >
        <form
          id={newListFormId}
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            createList.mutate(newListName, {
              onSuccess: (id) => {
                setActiveId(id)
                setNewListName('')
                setNewListOpen(false)
              },
              onError: (error) => {
                const feature = subscriptionRequiredFeature(error)
                if (feature === null) return
                setNewListOpen(false)
                upgradeToast(feature, seePlans)
              },
            })
          }}
        >
          <Field
            label="List name"
            maxLength={40}
            required
            value={newListName}
            onChange={(event) => {
              setNewListName(event.target.value)
            }}
          />
          {createList.isError && subscriptionRequiredFeature(createList.error) === null ? (
            <p role="alert" className="text-body-s text-destructive">
              {createList.error.message}
            </p>
          ) : null}
        </form>
      </Modal>

      {/* The list SWITCHER is gone from here — the sidebar's WATCHLIST
          group is it now (`watchlistGroup()`, `nav-groups.ts`). This
          screen only reads which list is current off the URL and
          renders it: a search field of its own, then one card holding
          every row. No per-list name/count heading either — the
          sidebar's own lit row already says which list this is. */}
      <div className="flex flex-col gap-4">
        <SearchInput
          aria-label="Search this list"
          placeholder="Search this list"
          // rest:ub — the mock's 42 (44 on the grid), edge only on hover and focus, 24 under the title
          // row and 32 above the list (the column's 16 gap plus this margin).
          className="mt-2 mb-4 border-transparent hover:border-border lg:h-11"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
          }}
        />
        {/* No card (mock): the rows sit on the canvas, each under its own hairline. */}
        <div>
          {active === undefined || active.rows.length === 0 ? (
            // `roomy` is the kit's mock empty page (160 mascot, 16px body, 120 above and below).
            <EmptyState
              kind="empty"
              size="section"
              scale="roomy"
              art={mitraEmptyArt}
              title="Nothing on this list yet"
              description="Add a company to see its price here."
              action={<Button onClick={openAdd}>Add company</Button>}
            />
          ) : (
            <>
              {visibleRows.length === 0 ? (
                <p className="border-t border-border-subtle py-6 text-body-m text-muted-foreground">
                  No names on {active.name} match “{search.trim()}”.
                </p>
              ) : (
                <div>
                  {visibleRows.map((row) => (
                    <WatchlistRow
                      key={row.symbol}
                      row={row}
                      listName={active.name}
                      moveTargets={lists.filter((list) => list.id !== active.id)}
                      expanded={row.symbol === expandedSymbol}
                      onToggle={() => {
                        setExpandedSymbol((current) =>
                          current === row.symbol ? undefined : row.symbol,
                        )
                      }}
                      onAddAlert={() => {
                        addAlertFor(row.symbol)
                      }}
                      onMove={(targetId) => {
                        void moveToList(row.symbol, targetId)
                      }}
                      onRemove={() => {
                        removeFromActiveList(row.symbol)
                      }}
                    />
                  ))}
                </div>
              )}
              <div className="flex justify-end border-t border-border-subtle pt-4">
                <Button type="button" aria-haspopup="dialog" className="h-10" onClick={openAdd}>
                  <Plus className="mr-2 size-4" aria-hidden="true" /> Add a company
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </ScreenLayout>
  )
}
